import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import Slider from '@wix/da-ds/pkg/Slider';
import SellPromotionCard from '@wix/da-shared-react/pkg/ShopCard/SellPromotionCard';

import s from './RecommendedSellSlider.scss';

interface Props {
  deviations: PapiDeviation[];
}

const RecommendedSellSlider: FC<Props> = ({ deviations }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const cardWidth = 264;
  const cards = useMemo(
    () =>
      deviations.map(deviation => (
        <SellPromotionCard
          key={`sell-${deviation.deviationId}`}
          deviation={deviation}
          width={cardWidth}
        />
      )),
    [deviations]
  );

  return (
    <div className={s['root']}>
      <div className={s['heading']}>
        <div className={s['title']}>{t('studio.products.sellStrip.title')}</div>
        {!isMobile && (
          <TextButton
            className={s['cta']}
            variant="main"
            size="medium"
            target="_blank"
            href={t('studio.products.sellStrip.cta.link')}
          >
            {t('studio.products.sellStrip.cta')}
          </TextButton>
        )}
        <div className={s['subtitle']}>
          {isMobile
            ? t('studio.products.sellStrip.subtitle.withCta', {
                postProcess: ['reactPostprocessor'],
                learnMoreLink: (
                  <a
                    className={s['cta-link']}
                    target="_blank"
                    href={t('studio.products.sellStrip.cta.link')} rel="noreferrer"
                  >
                    {t('studio.products.sellStrip.cta.mobile')}
                  </a>
                ),
              })
            : t('studio.products.sellStrip.subtitle')
          }
        </div>
      </div>
      <div className={s['slider']}>
        <Slider scrollBy={cardWidth * 4} className={s['custom-spacing']}>
          {cards}
        </Slider>
      </div>
    </div>
  );
};
RecommendedSellSlider.displayName = 'RecommendedSellSlider';

export default RecommendedSellSlider;
